import { escapeHtml as escapeHtmlShared } from '@theme/text-utils';
import { registerImpressionTarget } from '@theme/searchspring-beacon';
import { FILTER_FIELDS } from '@theme/searchspring-filter-logic';

/**
 * @typedef {Object} SearchspringPriceRange
 * @property {string | number} size - Size identifier (* for all sizes)
 * @property {number} price - Current price
 * @property {number} compareAtPrice - Original price for comparison
 *
 * @typedef {Record<string, SearchspringPriceRange[]>} SearchspringColorPrices
 * @typedef {Record<string, SearchspringColorPrices>} SearchspringPrices
 *
 * @typedef {Object} ExtendedSearchspringColorOption
 * @property {string} key
 * @property {string} [color]
 * @property {string | null} [label]
 * @property {string[]} [images]
 *
 * @typedef {Object} ExtendedSearchspringProductOptions
 * @property {ExtendedSearchspringColorOption[]} [colors] - Available color options
 * @property {Record<string, string[]>} [sizes] - Size groups organized by color (string sizes)
 * @property {string[]} [allSizes] - All available sizes across colors (string sizes)
 *
 * @typedef {Object} ExtendedSearchspringProduct
 * @property {string} [id] - Product ID
 * @property {string} [title] - Product title
 * @property {string} [handle] - Product handle
 * @property {string} [product_type] - Product type
 * @property {any} [options] - Raw product options
 * @property {any} [prices] - Processed price data
 * @property {any} [mtoPrices] - MTO-only price data (colors/sizes available for Made to Order)
 * @property {any} [rtsPrices] - RTS-only price data (colors/sizes available for Ready to Ship)
 * @property {Record<string, any>} [pricesByType] - Per-type price data keyed by product type, limited to the requested types
 * @property {any} [parsedOptions] - Parsed options data
 * @property {ExtendedSearchspringProductOptions} [searchspringOptions] - Searchspring-specific options
 * @property {string} [mfield_searchspring_price] - Raw price metafield value
 * @property {string[]} [mfield_searchspring_colors] - Colors array ["Name:#hex", ...]
 * @property {string[]} [mfield_searchspring_color_sizes] - Color sizes array ["Color-Size", ...]
 * @property {string[]} [mfield_searchspring_color_groups] - Color groups array
 * @property {string} [mfield_searchspring_sizes] - Sizes string
 * @property {string | Record<string, string[]>} [mfield_searchspring_variants_gallery] - Variants gallery (JSON string or parsed object)
 */

// Constants needed by the classes
const CONSTANTS = {
  PRODUCT_TYPES: {
    MTO: 'Made to Order',
    RTS: 'Ready to Ship',
    BOTH: 'both',
  },
  FILTER_FIELDS,
  SIZES_ORDER: [
    'xxxs',
    '3xs',
    'xxs',
    '2xs',
    'xs',
    's',
    'm',
    'l',
    'xl',
    'xxl',
    '2xl',
    'xxxl',
    '3xl',
    '4xl',
    '5xl',
    'n/a',
    'n-a',
  ],
};

export { CONSTANTS };

/**
 * Compare two size values with consistent Searchspring ordering rules.
 *
 * Rules:
 * - Named sizes follow CONSTANTS.SIZES_ORDER.
 * - "00" is placed after named sizes and before numeric sizes.
 * - "*" will be forced to the end of the list.
 * - Numeric sizes are sorted numerically.
 * - Fallback is locale string comparison.
 *
 * @param {string | number | null | undefined} a
 * @param {string | number | null | undefined} b
 * @returns {number}
 */
export function compareSearchspringSizeValues(a, b) {
  const aString = String(a ?? '').trim();
  const bString = String(b ?? '').trim();

  if (aString === '*' && bString !== '*') return 1;
  if (bString === '*' && aString !== '*') return -1;

  const indexA = CONSTANTS.SIZES_ORDER.indexOf(aString.toLowerCase());
  const indexB = CONSTANTS.SIZES_ORDER.indexOf(bString.toLowerCase());
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;

  if (aString === '00' && bString !== '00') return -1;
  if (bString === '00' && aString !== '00') return 1;

  const numA = Number(aString);
  const numB = Number(bString);
  if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;

  return aString.localeCompare(bString);
}

export class ProductDataProcessor {
  /**
   * Process raw product data into formatted products
   * @param {any[]} productsData - Raw product data from API
   * @param {string} [productTypeToUse] - Product type filter
   * @returns {ExtendedSearchspringProduct[]} Formatted product data
   */
  formatProducts(productsData, productTypeToUse = CONSTANTS.PRODUCT_TYPES.BOTH) {
    return productsData.map(
      /** @param {any} product */
      (product) => {
        const options = this.decodeData(product.options);
        const prices = this.parsePrices(product);
        const formattedPrices = this.formatPriceRanges(prices);
        const filteredPrices = this.filterPricesByType(formattedPrices, productTypeToUse);
        // Unmerged per-type prices (limited to productTypeToUse) for the card's
        // style-level price range — the merged `prices` drops MTO sizes that RTS also has.
        /** @type {Record<string, any>} */
        const pricesByType = {};
        for (const type of [CONSTANTS.PRODUCT_TYPES.RTS, CONSTANTS.PRODUCT_TYPES.MTO]) {
          if (productTypeToUse === CONSTANTS.PRODUCT_TYPES.BOTH || productTypeToUse === type) {
            pricesByType[type] = this.filterPricesByType(formattedPrices, type);
          }
        }
        let url = product.url ?? null;
        if (!url && product.handle) {
          url = `/products/${product.handle}`;
        }

        return {
          ...product,
          prices: filteredPrices,
          pricesByType,
          parsedOptions: options,
          url,
        };
      }
    );
  }

  /**
   * Add Searchspring-specific options to products
   * @param {ExtendedSearchspringProduct[]} products - Products to enhance
   * @returns {ExtendedSearchspringProduct[]} Products with Searchspring options
   */
  mapProductOptions(products) {
    return products.map((product) => this.addSearchspringOptions(product));
  }

  /**
   * Decode HTML entities in a string
   * @param {string} str - String with HTML entities
   * @returns {string} Decoded string
   */
  decodeHtmlEntities(str) {
    if (!str || typeof str !== 'string') return '';
    return str.replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  }

  /**
   * Safely parse JSON with HTML entity decoding
   * @template T
   * @param {string | T} data - JSON string or already parsed object
   * @param {T} fallback - Fallback value if parsing fails
   * @returns {T} Parsed data or fallback
   */
  safeParseJson(data, fallback) {
    if (!data) return fallback;
    if (typeof data === 'object') return data;
    if (typeof data !== 'string') return fallback;

    try {
      return JSON.parse(this.decodeHtmlEntities(data));
    } catch {
      return fallback;
    }
  }

  /**
   * Decode HTML-encoded JSON data
   * @param {string} data - Encoded JSON string
   * @returns {any} Parsed JSON data
   */
  decodeData(data) {
    if (!data) return {};
    try {
      return JSON.parse(this.decodeHtmlEntities(data));
    } catch (error) {
      console.warn('Failed to parse JSON data:', error);
      return {};
    }
  }

  /**
   * Parse price data from metafield
   * @param {ExtendedSearchspringProduct} product - Product with price metafield
   * @returns {Record<string, any>} Parsed price data
   */
  parsePrices(product) {
    return /** @type {Record<string, any>} */ (this.safeParseJson(product?.mfield_searchspring_price, {}));
  }

  /**
   * Format raw price data into structured price ranges
   * @param {Record<string, any>} pricesObj - Raw price data
   * @returns {SearchspringPrices} Formatted price ranges
   */
  formatPriceRanges(pricesObj = {}) {
    /** @type {SearchspringPrices} */
    const result = {};

    for (const category of Object.keys(pricesObj)) {
      const colorsObj = pricesObj[category];
      if (typeof colorsObj !== 'object' || colorsObj === null) continue;

      result[category] = {};

      for (const color of Object.keys(colorsObj)) {
        const entries = colorsObj[color];
        if (!Array.isArray(entries)) continue;

        result[category][color] = entries.map((entry) => {
          if (typeof entry !== 'string') return { size: '*', price: NaN, compareAtPrice: NaN };
          const [sizeStr, priceStr, compareStr] = entry.split(':');
          return {
            size: sizeStr === '*' ? '*' : (sizeStr ?? ''),
            price: Number(priceStr),
            compareAtPrice: Number(compareStr),
          };
        });
      }
    }

    return result;
  }

  /**
   * Filter prices by product type (MTO, RTS, or both)
   * @param {any} pricesObj - Price data to filter
   * @param {string} type - Product type filter
   * @returns {any} Filtered price data
   */
  filterPricesByType(pricesObj = {}, type = CONSTANTS.PRODUCT_TYPES.BOTH) {
    /** @type {(obj1: any, obj2: any) => any} */
    const mergeObjects = (obj1, obj2) => {
      /** @type {Record<string, any>} */
      const result = {};

      /** @type {(arr?: any[]) => Record<string, any>} */
      const toSizeMap = (arr = []) => Object.fromEntries(arr.map((item) => [item.size, item]));

      /** @type {(map: Record<string, any>) => any[]} */
      const toArray = (map) => Object.values(map);

      const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

      for (const key of allKeys) {
        const map1 = toSizeMap(obj1?.[key]);
        const map2 = toSizeMap(obj2?.[key]);

        const merged = { ...map2 };
        for (const size in map1) {
          merged[size] = map1[size];
        }

        result[key] = toArray(merged);
      }

      return result;
    };

    if (type === CONSTANTS.PRODUCT_TYPES.BOTH) {
      const rtsPrices = pricesObj[CONSTANTS.PRODUCT_TYPES.RTS] || {};
      const mtoPrices = pricesObj[CONSTANTS.PRODUCT_TYPES.MTO] || {};
      return mergeObjects(rtsPrices, mtoPrices);
    }

    return pricesObj[type] || {};
  }

  /**
   * Add Searchspring-specific options to a product
   * @param {ExtendedSearchspringProduct} product - Product to enhance
   * @returns {ExtendedSearchspringProduct} Product with Searchspring options
   */
  addSearchspringOptions(product) {
    const colorsArr = product.mfield_searchspring_colors;
    const colorSizesArr = product.mfield_searchspring_color_sizes;
    const galleryStr = product.mfield_searchspring_variants_gallery;

    if (!colorsArr || !Array.isArray(colorsArr) || colorsArr.length === 0) return product;

    /** @type {Record<string, string>} */
    const colorsMap = {};
    colorsArr.forEach((item) => {
      const [name, hex] = item.split(':');
      if (name) colorsMap[name] = hex || '';
    });

    const gallery = /** @type {Record<string, string[]>} */ (this.safeParseJson(galleryStr, {}));
    const colorOptions = this.buildColorOptions(colorsMap, gallery);
    const { sizeGroups, allSizes } = this.buildSizeGroupsFromArray(colorSizesArr || []);

    product.searchspringOptions = {
      colors: /** @type {ExtendedSearchspringColorOption[]} */ (colorOptions),
      sizes: sizeGroups,
      allSizes,
    };

    return product;
  }

  /**
   * Build color options from metafield data
   * @param {Record<string, string>} colorsMap - Color key-value pairs
   * @param {Record<string, string[]>} gallery - Gallery data
   * @returns {ExtendedSearchspringColorOption[]} Array of color options
   */
  buildColorOptions(colorsMap, gallery) {
    /**
     * Extract all image URLs from gallery array
     * @param {string[]} arr - Gallery entries
     * @returns {string[]} Array of image URLs
     */
    const extractImages = (arr) => {
      if (!arr || !Array.isArray(arr) || arr.length === 0) return [];
      return arr
        .map((entry) => {
          if (typeof entry !== 'string') return '';
          const separatorIndex = entry.indexOf(':-:');
          if (separatorIndex === -1) return entry;
          return entry.substring(separatorIndex + 3);
        })
        .filter(Boolean);
    };

    return Object.keys(colorsMap).map((key) => {
      const images = extractImages(gallery[key] || []);
      return {
        key,
        color: colorsMap[key] || '',
        label: key,
        images,
      };
    });
  }

  /**
   * Build size groups from color_sizes array
   * @param {string[]} colorSizesArr - Array of "Color-Size" strings like ["*-00", "Agave-XS", ...]
   * @returns {{ sizeGroups: Record<string, string[]>; allSizes: string[] }} Size groups and all sizes (as strings)
   */
  buildSizeGroupsFromArray(colorSizesArr) {
    /** @type {Record<string, string[]>} */
    const sizeGroups = {};
    /** @type {Set<string>} */
    const allSizesSet = new Set();
    /** @type {Set<string>} */
    const wildcardColors = new Set();

    if (!colorSizesArr || !Array.isArray(colorSizesArr)) {
      return { sizeGroups, allSizes: [] };
    }

    colorSizesArr.forEach((/** @type {string} */ entry) => {
      if (typeof entry !== 'string') return;
      const separatorIndex = entry.lastIndexOf('-');
      const color = separatorIndex !== -1 ? entry.substring(0, separatorIndex) : '';
      const size = separatorIndex !== -1 ? entry.substring(separatorIndex + 1).trim() : '';

      if (size === '*' || !size) {
        if (color !== '*') wildcardColors.add(color);
        return;
      }

      if (color === '*') {
        allSizesSet.add(size);
        return;
      }

      if (!sizeGroups[color]) sizeGroups[color] = [];
      sizeGroups[color].push(size);
      allSizesSet.add(size);
    });

    Object.keys(sizeGroups).forEach((color) => {
      if (sizeGroups[color]) {
        sizeGroups[color].sort(compareSearchspringSizeValues);
      }
    });

    const allSizes = /** @type {string[]} */ (Array.from(allSizesSet).sort(compareSearchspringSizeValues));

    wildcardColors.forEach((color) => {
      sizeGroups[color] = allSizes;
    });

    return { sizeGroups, allSizes };
  }
}

/**
 * Build a map from color code and color label
 * @param {string[]} colorLabelsArr
 * @returns {Map<string, string>}
 */
export function buildColorCodeToLabelMap(colorLabelsArr) {
  if (!colorLabelsArr || !Array.isArray(colorLabelsArr)) return new Map();

  /** @type {Map<string, string>} */
  const map = new Map();
  for (const item of colorLabelsArr) {
    if (typeof item !== 'string') continue;
    const colonIndex = item.indexOf(':');
    if (colonIndex === -1) continue;
    const code = item.substring(0, colonIndex).trim();
    const label = item.substring(colonIndex + 1).trim();
    if (code && label) {
      map.set(code, label);
    }
  }
  return map;
}

/**
 * Build a map from lowercase color label → color code.
 * @param {string[]} colorLabelsArr
 * @returns {Map<string, string>}
 */
export function buildColorLabelToCodeMap(colorLabelsArr) {
  if (!colorLabelsArr || !Array.isArray(colorLabelsArr)) return new Map();

  /** @type {[string, string][]} */
  const entries = [];
  for (const item of colorLabelsArr) {
    if (typeof item !== 'string') continue;
    const colonIndex = item.indexOf(':');
    if (colonIndex === -1) continue;
    const code = item.substring(0, colonIndex).trim();
    const label = item.substring(colonIndex + 1).trim();
    if (code && label) {
      entries.push([label.toLowerCase(), code]);
    }
  }

  entries.sort((a, b) => b[0].length - a[0].length);
  return new Map(entries);
}

/**
 * Detect an individual color
 * @param {string} searchTerms
 * @param {Map<string, string>} colorMap
 * @returns {string | null}
 */
export function detectColorFromLabels(searchTerms, colorMap) {
  if (!searchTerms || !colorMap || colorMap.size === 0) return null;
  const query = searchTerms.toLowerCase();
  for (const [label, code] of colorMap) {
    if (query.includes(label) || query.includes(code.toLowerCase())) return code;
  }
  return null;
}

/**
 * Detect color GROUP(s) from the search query.
 *
 * Matches each space-delimited word part of the query against color group
 * names (e.g. "green dress" → the "green" group). Every matching group's
 * codes are collected so that any product carrying a color in that group can
 * be promoted. Matching is word-part based (not substring) so "altered" does
 * not match the "red" group.
 *
 * @param {string} searchTerms
 * @param {Record<string, string[]>} colorGroupsMap - lowercase keys, e.g. { "red": ["ADM","ALP",...], "blue": ["BLUE","BST",...] }
 * @returns {string[] | null}
 */
export function detectColorGroupFromQuery(searchTerms, colorGroupsMap) {
  if (!searchTerms || !colorGroupsMap) return null;

  const tokens = searchTerms
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.replace(/[^a-z0-9]+/g, ''))
    .filter(Boolean);
  if (tokens.length === 0) return null;

  /** @type {string[]} */
  const codes = [];
  const seen = new Set();
  for (const token of tokens) {
    const groupCodes = colorGroupsMap[token];
    if (!groupCodes) continue;
    for (const code of groupCodes) {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    }
  }
  return codes.length > 0 ? codes : null;
}

// Yield helper to yield execution back to the main thread
const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Turn a banner's destination into readable link text, e.g.
 * "/collections/rehearsal-dinner?x=1" -> "Rehearsal dinner".
 *
 * @param {string | null} href
 * @returns {string}
 */
function labelFromHref(href) {
  if (!href) return '';
  const slug = href.split(/[?#]/)[0].split('/').filter(Boolean).pop() ?? '';

  // A slug with a stray '%' (e.g. "save-50%-sale") makes decodeURIComponent throw, and
  // this runs inside the unguarded chunk loop that renders the product grid.
  let decoded = slug;
  try {
    decoded = decodeURIComponent(slug);
  } catch {
    // Keep the raw slug; a slightly uglier label beats losing the grid.
  }

  const words = decoded.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * Give a merchandising banner an accessible name.
 *
 * Banner markup is authored in the Searchspring admin, where the image alt text is
 * routinely left blank. That ships an unnamed image inside an unnamed link — axe flags it
 * as both `image-alt` and `link-name` (WCAG 1.1.1 and 2.4.4) and a screen reader announces
 * only "link". Named banners are still the merchant's job; this is the safety net.
 *
 * @param {HTMLElement} bannerElement
 */
function ensureBannerAccessibleName(bannerElement) {
  const image = bannerElement.querySelector('img');
  const link = bannerElement.querySelector('a');
  const authoredAlt = image?.getAttribute('alt')?.trim();

  // A merchant-authored alt already names the link, and aria-label would outrank and
  // silently replace it. Leave well-named banners alone — this is only a safety net.
  if (authoredAlt) return;

  if (image) {
    // Inside a link the image adds nothing the link text does not, so mark it decorative
    // and name the link instead; a standalone banner image has to describe itself.
    image.setAttribute('alt', link ? '' : image.getAttribute('title')?.trim() || '');
  }

  if (!link || link.getAttribute('aria-label') || link.textContent.trim()) return;

  const label = image?.getAttribute('title')?.trim() || labelFromHref(link.getAttribute('href'));
  if (label) link.setAttribute('aria-label', label);
}

// Product Renderer Class
export class ProductRenderer {
  /** @type {HTMLElement} */
  container;

  // Track current render to allow cancellation of older render requests
  currentRenderId = 0;

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
  }

  /**
   * Render products in a carousel format
   * @param {SearchspringProduct[]} products - Products to render
   * @param isCarousel
   * @param append
   * @param {{ activeColorFilters?: string[], activeAvailabilityFilter?: string | null, activeSizeFilter?: string | null, banners?: any[], colorCodeToLabelMap?: Map<string, string>, promotedColors?: string[], promotedColorsMode?: string, isB2B?: boolean }} [options]
   */
  async renderProducts(products, isCarousel = true, append = false, options = {}) {
    this.currentRenderId++;
    const renderId = this.currentRenderId;

    const hasExactSlidesDesktop = this.container.hasAttribute('data-exact-slides-desktop');
    if (hasExactSlidesDesktop) {
      this.container.classList.add('searchspring-recommendations--exact-slides-desktop');
    } else {
      this.container.classList.remove('searchspring-recommendations--exact-slides-desktop');
    }

    if (!isCarousel) {
      if (!append) {
        const gridTemplate = this.createGridTemplate();
        this.container.innerHTML = gridTemplate;
      }
      await this.renderProductGrid(products, options, renderId);
      return;
    }
    const paginationProducts = products.length > 1 ? products.slice(0, -1) : products;
    const controlsTemplate = this.createControlsTemplate(paginationProducts);
    const header = this.container.querySelector('[data-carousel-header]');
    const exactSlidesDesktop = this.container.hasAttribute('data-exact-slides-desktop');
    const infinite = this.container.hasAttribute('data-infinite');
    const carouselTemplate = this.createCarouselTemplate(
      header,
      controlsTemplate,
      paginationProducts,
      exactSlidesDesktop,
      infinite
    );

    this.container.innerHTML = carouselTemplate;
    await this.renderSlides(products, options, renderId);
    
    if (renderId === this.currentRenderId) {
      // Not awaited: the cards are in, so the caller carries on (e.g. reports
      // impressions) while slideshow.js may still be loading.
      this.initializeSlideshow();
    }
  }

  /**
   * Create pagination controls template
   * @param {SearchspringProduct[]} products - Products for pagination
   * @returns {string} HTML template for controls
   */
  createControlsTemplate(products) {
    return products
      .map(
        (product) => `
                    <button
                        class="slideshow-control slideshow-control--bar"
                        aria-label="Go to ${escapeHtmlShared(product.title || '')}"
                        data-product-id="${escapeHtmlShared(product.id || '')}"
                    ></button>
                `
      )
      .join('');
  }

  /**
   * Create the main carousel template
   * @param {Element | null} header - Header element to include
   * @param {string} controlsTemplate - Pagination controls HTML
   * @param {SearchspringProduct[]} paginationProducts - Products for pagination
   * @param {boolean} exactSlidesDesktop - Whether to use exact slides on desktop (no peek)
   * @param {boolean} infinite - Whether the arrows loop from the last slide back to the first
   * @returns {string} Complete carousel HTML template
   */
  createCarouselTemplate(
    header,
    controlsTemplate,
    paginationProducts,
    exactSlidesDesktop = false,
    infinite = false
  ) {
    const exactSlidesClass = exactSlidesDesktop ? ' searchspring-recommendations--exact-slides-desktop' : '';
    const infiniteAttribute = infinite ? ' infinite' : '';
    return `
            <div class="resource-list force-full-width resource-list__carousel${exactSlidesClass}">
                <div class="resource-list__carousel with-arrows-in-header" style="--gutter-slide-width: var(--util-page-margin-offset);--slide-width-max: 300px;">
                    <slideshow-component deferred${infiniteAttribute} mobile-pagination mobile-two-products ref="resourceListCarousel" class="resource-list__carousel" initial-slide="0" disabled="false" paused="" actioned="">
                        <slideshow-container ref="slideshowContainer">
                            ${header ? header.outerHTML : ''}
                            <slideshow-slides class="slideshow-slides-main-container" tabindex="-1" ref="scroller" gutters="start end">
                            </slideshow-slides>
                        </slideshow-container>
                        <slideshow-controls class="mobile-pagination-controls">
                            <div class="slideshow-controls__bars">
                                ${paginationProducts.length > 2 ? controlsTemplate : ''}
                            </div>
                        </slideshow-controls>
                    </slideshow-component>
                </div>
            </div>
        `;
  }

  createGridTemplate() {
    return `
    <div id="searchspring-product-grid" ref="grid" class="searchspring-product-grid"></div>
    `
  }

  /**
   * Render individual product slides
   * @param {SearchspringProduct[]} products - Products to render as slides
   * @param {{ promotedColors?: string[], promotedColorsMode?: string, isB2B?: boolean }} [options]
   * @param {number} renderId - ID of this render session
   */
  async renderSlides(products, options = {}, renderId) {
    const slidesContainer = this.container.querySelector('slideshow-slides');
    if (!slidesContainer) return;

    const { promotedColors, promotedColorsMode, isB2B } = options;
    const CHUNK_SIZE = 4;

    for (let i = 0; i < products.length; i += CHUNK_SIZE) {
      if (renderId !== this.currentRenderId) return;

      const chunk = products.slice(i, i + CHUNK_SIZE);
      const fragment = document.createDocumentFragment();

      chunk.forEach((product) => {
        const slide = document.createElement('slideshow-slide');
        slide.setAttribute('ref', 'slides[]');
        slide.setAttribute('aria-hidden', 'false');
        slide.classList.add('resource-list__slide');

        const productCard = /** @type {any} */ (document.createElement('searchspring-product-card'));
        productCard.product = product;
        if (product?.uid) {
          productCard.setAttribute('data-product-uid', String(product.uid));
          productCard.setAttribute('data-product-sku', String(product.sku || ''));
        }
        if (promotedColors && promotedColors.length > 0) {
          productCard.promotedColors = promotedColors;
          productCard.promotedColorsMode = promotedColorsMode || 'filter';
        }
        if (isB2B) {
          productCard.isB2B = true;
        }
        slide.appendChild(productCard);
        fragment.appendChild(slide);
      });

      slidesContainer.appendChild(fragment);

      if (i + CHUNK_SIZE < products.length) {
        await yieldToMain();
      }
    }
  }

  /**
   * @param {SearchspringProduct[]} products
   * @param {{ activeColorFilters?: string[], activeAvailabilityFilter?: string | null, activeSizeFilter?: string | null, banners?: any[], colorCodeToLabelMap?: Map<string, string>, promotedColors?: string[], promotedColorsMode?: string, isB2B?: boolean }} [options]
   * @param {number} renderId - ID of this render session
   */
  async renderProductGrid(products, options = {}, renderId) {
    const gridContainer = this.container.querySelector('#searchspring-product-grid');
    if (!gridContainer) return;

    const { activeColorFilters = [], activeAvailabilityFilter = null, activeSizeFilter = null, banners = [], colorCodeToLabelMap, promotedColors, promotedColorsMode, isB2B } = options;

    const renderItems = [];

    // Interleave banners and products into a flat array first to maintain layout sequencing
    if (banners && banners.length > 0) {
      const bannersByPosition = new Map();
      const bannerPositions = [];

      banners.forEach((banner) => {
        let position = Number.parseInt(String(banner.positionIndex ?? '0'), 10);
        if (Number.isNaN(position)) {
          position = 0;
        }
        if (position < 0) {
          console.warn('Banner has negative positionIndex; clamping to 0:', banner);
          position = 0;
        }
        if (!bannersByPosition.has(position)) {
          bannersByPosition.set(position, []);
        }
        bannersByPosition.get(position).push(banner);
        bannerPositions.push(position);
      });

      const maxBannerPosition = bannerPositions.length ? Math.max(...bannerPositions) : -1;
      const maxIndex = Math.max(products.length - 1, maxBannerPosition);
      let productIndex = 0;

      for (let itemIndex = 0; itemIndex <= maxIndex; itemIndex++) {
        if (bannersByPosition.has(itemIndex)) {
          const bannersAtPosition = bannersByPosition.get(itemIndex) || [];
          bannersAtPosition.forEach((banner) => {
            renderItems.push({ type: 'banner', data: banner });
          });
          bannersByPosition.delete(itemIndex);
        }

        if (productIndex < products.length) {
          const product = products[productIndex];
          if (product) {
            renderItems.push({ type: 'product', data: product });
          }
          productIndex++;
        }
      }

      bannersByPosition.forEach((bannersAtPosition) => {
        bannersAtPosition.forEach((banner) => {
          renderItems.push({ type: 'banner', data: banner });
        });
      });
    } else {
      products.forEach((product) => {
        renderItems.push({ type: 'product', data: product });
      });
    }

    const CHUNK_SIZE = 4;
    for (let i = 0; i < renderItems.length; i += CHUNK_SIZE) {
      if (renderId !== this.currentRenderId) return;

      const chunk = renderItems.slice(i, i + CHUNK_SIZE);
      const fragment = document.createDocumentFragment();

      chunk.forEach((item) => {
        if (item.type === 'banner') {
          this.renderBanner(fragment, item.data);
        } else {
          this.renderProductCard(
            fragment,
            item.data,
            activeColorFilters,
            activeAvailabilityFilter,
            activeSizeFilter,
            colorCodeToLabelMap,
            promotedColors,
            promotedColorsMode,
            gridContainer,
            isB2B
          );
        }
      });

      gridContainer.appendChild(fragment);

      if (i + CHUNK_SIZE < renderItems.length) {
        await yieldToMain();
      }
    }
  }

  /**
   * Render a single product card
   * @param {HTMLElement | DocumentFragment} container
   * @param {SearchspringProduct} product
   * @param {string[]} activeColorFilters
   * @param {string | null} activeAvailabilityFilter
   * @param {string | null} activeSizeFilter
   * @param {Map<string, string>} [colorCodeToLabelMap]
   * @param {string[]} [promotedColors]
   * @param {string} [promotedColorsMode]
   * @param {HTMLElement | null} [targetContainer]
   * @param {boolean} [isB2B]
   */
  renderProductCard(container, product, activeColorFilters, activeAvailabilityFilter, activeSizeFilter, colorCodeToLabelMap, promotedColors, promotedColorsMode, targetContainer = null, isB2B = false) {
    const productCard = /** @type {any} */ (document.createElement('searchspring-product-card'));
    productCard.product = product;
    if (product?.uid) {
      productCard.setAttribute('data-product-uid', String(product.uid));
      productCard.setAttribute('data-product-sku', String(product.sku || ''));
    }
    if (activeColorFilters && activeColorFilters.length > 0) {
      productCard.activeColorFilters = activeColorFilters;
    }
    if (activeAvailabilityFilter) {
      productCard.activeAvailabilityFilter = activeAvailabilityFilter;
    }
    if (activeSizeFilter) {
      productCard.activeSizeFilter = activeSizeFilter;
    }
    if (colorCodeToLabelMap && colorCodeToLabelMap.size > 0) {
      productCard.colorCodeToLabelMap = colorCodeToLabelMap;
    }
    if (promotedColors && promotedColors.length > 0) {
      productCard.promotedColors = promotedColors;
      productCard.promotedColorsMode = promotedColorsMode || 'filter';
    }
    if (isB2B) {
      productCard.isB2B = true;
    }
    
    // Compute fold position globally by checking both target container and current fragment/container
    const existingCount = targetContainer ? targetContainer.querySelectorAll('searchspring-product-card').length : 0;
    const currentFragmentCount = container.querySelectorAll('searchspring-product-card').length;
    const cardPosition = existingCount + currentFragmentCount;
    productCard.aboveTheFold = cardPosition < 4;
    
    container.appendChild(productCard);
  }

  /**
   * Render a banner element
   * @param {HTMLElement | DocumentFragment} container
   * @param {any} bannerData - Banner data with content in `value` property
   */
  renderBanner(container, bannerData) {
    const bannerElement = document.createElement('div');
    bannerElement.className = 'searchspring-banner';
    bannerElement.setAttribute('data-position', bannerData.positionIndex || '0');

    // The banner HTML content is stored in the `value` property
    if (bannerData.value) {
      // Remove the <script> tag and extract only the actual content
      let content = bannerData.value;

      // Searchspring prefixes each banner with an inert `<script type="text/widget">`
      // marker carrying the banner ID. Read it before the tag is discarded — it is
      // what the Athos beacon and GA4 need to identify the banner.
      const bannerIdMatch = content.match(/data-banner-id="([^"]+)"/);
      const bannerId = bannerIdMatch ? bannerIdMatch[1] : null;

      // Find the closing </script> tag and get everything after it
      const scriptEndIndex = content.indexOf('</script>');
      if (scriptEndIndex !== -1) {
        content = content.substring(scriptEndIndex + 9).trim();
      }

      // if there is an image with alt text - get it
      const imgTagMatch = content.match(/<img[^>]+alt="([^">]+)"[^>]*>/i);
      if (imgTagMatch && imgTagMatch[1]) {
        const parts = imgTagMatch[1].split('::');
        if (parts.length === 2) {
          const colSpan = parts[0].trim();
          const altText = parts[1].trim();

          bannerElement.style.setProperty('--col-span', colSpan);
          // replace alt text on the image
          content = content.replace(/(<img[^>]+alt=")[^">]+(")/i, `$1${altText}$2`);
        }
      }

      // Safely inject the HTML content
      if (content) {
        bannerElement.innerHTML = content;
      }

      // if there is a title on the image - get it and insert as div
      const titleTagMatch = content.match(/<img[^>]+title="([^">]+)"[^>]*>/i);
      if (titleTagMatch && titleTagMatch[1]) {
        const titleText = titleTagMatch[1].trim();
        const titleDiv = document.createElement('div');
        titleDiv.className = 'searchspring-banner__title';
        titleDiv.textContent = titleText;

        const anchorTag = bannerElement.querySelector('& > a');
        if (anchorTag) {
          anchorTag.appendChild(titleDiv);
        } else {
          bannerElement.appendChild(titleDiv);
        }
      }

      if (bannerId) {
        bannerElement.setAttribute('data-banner-id', bannerId);
        this.trackBanner(bannerElement, bannerId);
      }
      ensureBannerAccessibleName(bannerElement);
    }

    container.appendChild(bannerElement);

    // Banners are plain divs rather than custom elements, so they have no
    // connectedCallback to self-register from.
    if (bannerElement.hasAttribute('data-banner-id')) {
      registerImpressionTarget(bannerElement);
    }

    const headerComponent = document.getElementById('header-component');
    const headerHeight = headerComponent ? headerComponent.offsetHeight : 0;
    bannerElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  /**
   * Wire up reporting for a merchandising banner.
   *
   * Searchspring has no banner click beacon, so credit is claimed through the
   * beacon's attribution mechanism instead: the Athos web pixel reads an
   * `ss_attribution=<type>:<id>` query param off the landing page and attaches it
   * to every event it sends for the rest of the session, which is what ties
   * downstream product views, add-to-carts and orders back to the banner.
   *
   * The click count itself goes to GA4 via a custom event, matching the
   * `popover_search` pattern in `main-search.js`.
   *
   * @param {HTMLElement} bannerElement
   * @param {string} bannerId - Searchspring's banner ID, from `data-banner-id`.
   */
  trackBanner(bannerElement, bannerId) {
    const links = /** @type {HTMLAnchorElement[]} */ ([
      ...bannerElement.querySelectorAll('a[href]'),
    ]);

    links.forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);

        // The pixel only sees the param if the shopper lands on our own domain,
        // and a merchandiser may already have set one on the banner's target URL
        // in the Searchspring admin — leave theirs alone.
        if (url.origin !== window.location.origin) return;
        if (url.searchParams.has('ss_attribution')) return;

        url.searchParams.set('ss_attribution', `banner:${bannerId}`);
        link.href = url.toString();
      } catch (e) {
        // A malformed href is not worth breaking the banner over.
      }
    });

    if (links.length === 0) return;

    bannerElement.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || !bannerElement.contains(link)) return;

      const image = bannerElement.querySelector('img');

      try {
        /** @type {any} */ (window).Shopify?.analytics?.publish('merchandising_banner_clicked', {
          bannerId,
          position: bannerElement.getAttribute('data-position') || '0',
          destination: /** @type {HTMLAnchorElement} */ (link).href,
          creative: image?.getAttribute('title') || image?.getAttribute('alt') || '',
        });
      } catch (e) {
        // Analytics must never break navigation.
      }
    });
  }

  /**
   * Initialize the slideshow component now, rather than on the first hover or
   * scroll: until it's set up, its arrows and pagination don't match the
   * slides (e.g. "previous" shows on the first slide).
   */
  async initializeSlideshow() {
    const slideshowComponent = this.container.querySelector('slideshow-component');
    if (!slideshowComponent) return;

    // slideshow.js loads only once a slideshow-component is on the page. Where
    // this carousel is the first (the cart page), it's still loading here, and
    // once defined, a `deferred` slideshow waits for this call to set it up.
    await customElements.whenDefined('slideshow-component');
    // A newer render may have replaced it meanwhile.
    if (!slideshowComponent.isConnected) return;

    if (
      slideshowComponent instanceof HTMLElement &&
      'setupSlideshow' in slideshowComponent &&
      typeof slideshowComponent.setupSlideshow === 'function'
    ) {
      // The slides were added after the component connected; refresh its refs to them.
      if (
        'updatedCallback' in slideshowComponent &&
        typeof slideshowComponent.updatedCallback === 'function'
      ) {
        slideshowComponent.updatedCallback();
      }
      slideshowComponent.setupSlideshow(false);
    }
  }
}
